import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { StateService } from '../../../core/services/state.service';

export type WebSocketDiagnosticStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

export interface WebSocketDiagnostics {
  status: WebSocketDiagnosticStatus;
  roadId: string | null;
  roadName: string | null;
  reconnectScheduledAt: number | null;
  lastConnectAttemptAt: number | null;
  connectedAt: number | null;
  disconnectedSinceAt: number | null;
  lastActivityAt: number | null;
  inactivityDetected: string | null;
  inactivityDetectedAt: number | null;
  lastErrorAt: number | null;
  lastErrorMessage: string | null;
  lastCloseReason: string | null;
  lastCloseAt: number | null;
  lastEvent: string;
  lastEventAt: number | null;
}

/**
 * Servicio para gestionar la conexión al websocket de alertas para una carretera específica
 * Incluye lógica para:
 * - Conexión al websocket con la carretera como parámetro
 * - Recepción de mensajes y emisión a través de un Subject
 * - Desconexión manual y automática
 * - Control de estado de conexión
 * - Reconexión automática con backoff exponencial y jitter ante cierres inesperados o errores
 * - Watchdog que revisa periódicamente la salud de la conexión
 * - Forzado de recarga de página como último recurso tras largos periodos desconectados 
 */
@Injectable({ providedIn: 'root' })
export class AlertsWebSocketService implements OnDestroy {
  private ws: WebSocket | null = null;
  private currentRoadId: string | null = null;
  private currentRoadName: string | null = null;

  private reconnectAttempts = 0; // contador de intentos de reconexión rápida
  private readonly fastReconnectAttempts = 5; // número de intentos máximo de reconexión rápida
  private readonly baseReconnectMs = 1000; // 1 seg para el primer intento, luego backoff exponencial hasta fastReconnectAttempts
  private readonly maxReconnectMs = 30_000; // 30 seg máximo para intentos rápidos
  private readonly slowReconnectMs = 30_000; // 30 seg para intentos lentos tras superar fastReconnectAttempts

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null; // timer para programar reconexiones
  private watchdogTimer: ReturnType<typeof setInterval> | null = null; // timer para el watchdog que revisa la salud de la conexión periódicamente

  private isManualClose = false; // flag para indicar si la desconexión fue solicitada manualmente, evitando programar reconexiones
  private destroyed = false; // flag para indicar si el servicio ha sido destruido, evitando reconexiones

  private lastConnectAttemptAt: number | null = null; // timestamp del último intento de conexión, usado para detectar sockets atascados en CONNECTING
  private disconnectedSinceAt: number | null = null; // timestamp de cuándo se detectó la desconexión, usado para valorar forzar recarga tras largos periodos desconectados
  private lastActivityAt: number | null = null; // timestamp del ultimo mensaje o actividad observada en el socket

  private hasForcedReload = false; // flag para evitar forzar recarga de página más de una vez durante la vida del servicio

  // Ajustes
  private readonly watchdogIntervalMs = 60_000; // intervalo del watchdog en milisegundos
  private readonly maxConnectingTimeMs = 20_000; // tiempo máximo permitido en estado CONNECTING antes de forzar reconexión (20 seg)
  private readonly defaultInactivityReconnectMs = 15 * 60_000; // 15 minutos sin actividad como umbral general
  private readonly peakInactivityReconnectMs = 5 * 60_000; // 5 minutos sin actividad en franjas horarias sensibles
  private readonly forceReloadAfterMs = 5 * 60_000; // tiempo desconectado tras el cual se fuerza recarga de página como último recurso (5 minutos)

  // Subjects para emitir mensajes recibidos
  private messageSubject = new Subject<any>();
  public messages$: Observable<any> = this.messageSubject.asObservable();

  // Subject para emitir el estado de conexión al websocket
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  public connectionStatus$: Observable<boolean> = this.connectionStatusSubject.asObservable();

  // Subject para exponer diagnostico detallado del websocket
  private diagnosticsSubject = new BehaviorSubject<WebSocketDiagnostics>(
    this.buildInitialDiagnosticsState()
  );
  public diagnostics$: Observable<WebSocketDiagnostics> = this.diagnosticsSubject.asObservable();


  constructor(private ngZone: NgZone, private router: Router, private stateService: StateService) {
    this.startWatchdog();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.disconnect();
    this.stopWatchdog();
  }

  // -------------------------
  // MÉTODOS PÚBLICOS
  // -------------------------

  /**
   * Método para gestionar las conexiones al websocket
   * @param roadId: identificador de la carretera para la que se quieren recibir alertas
   */
  connect(roadId: string, roadName: string | null = null): void {
    if (this.destroyed) return;

    const isNewRoad = this.currentRoadId !== roadId;
    const wsUrl = this.getWebSocketUrl(roadId);
    this.currentRoadName = roadName;

    if (isNewRoad) {
      this.currentRoadId = roadId;
      this.reconnectAttempts = 0;
      this.hasForcedReload = false;
      this.disconnectedSinceAt = null;
      this.resetActivityState();
      this.updateDiagnosticsState({
        roadId,
        roadName,
        reconnectScheduledAt: null,
        lastCloseReason: null,
        lastCloseAt: null
      });
    }

    if (!navigator.onLine) {
      console.warn('Sin conexión de red. No se abre WebSocket todavía.');
      this.markDisconnected('Sin conexion de red');
      return;
    }

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      this.updateDiagnosticsState({
        status: this.ws.readyState === WebSocket.OPEN ? 'connected' : 'connecting',
        roadId,
        roadName
      });
      return;
    }

    this.isManualClose = false;
    this.lastConnectAttemptAt = Date.now();

    this.clearReconnectTimer();
    this.closeSocketSilently();

    console.log('Conectando WebSocket para alertas: ', roadName, wsUrl);
    this.updateDiagnosticsState({
      status: 'connecting',
      roadId,
      roadName,
      reconnectScheduledAt: null,
      lastConnectAttemptAt: this.lastConnectAttemptAt,
      lastEvent: 'Intentando conexion',
      lastEventAt: this.lastConnectAttemptAt,
    });

    try {
      this.ws = new WebSocket(wsUrl);

      //Manejo de apertura de conexión
      this.ws.onopen = () => {
        const connectedAt = Date.now();
        console.log('WebSocket conectado para alertas en carretera: ', roadId, roadName);
        this.reconnectAttempts = 0;
        this.disconnectedSinceAt = null;
        this.recordSocketActivity(connectedAt);
        this.connectionStatusSubject.next(true);
        this.updateDiagnosticsState({
          status: 'connected',
          roadId,
          roadName,
          reconnectScheduledAt: null,
          connectedAt,
          disconnectedSinceAt: null,
          lastEvent: 'Conexion abierta',
          lastEventAt: connectedAt,
        });
      };

      // Manejo de mensajes entrantes
      this.ws.onmessage = (event) => {
        const lastMessageAt = Date.now();
        this.recordSocketActivity(lastMessageAt);
        try {
          const data = JSON.parse(event.data);
          this.messageSubject.next(data);
          this.updateDiagnosticsState({
            status: 'connected',
            lastActivityAt: lastMessageAt,
            lastEvent: 'Mensaje recibido',
            lastEventAt: lastMessageAt,
          });
        } catch (error) {
          console.warn('Error al procesar el mensaje recibido del websocket:', event.data, error);
          this.messageSubject.next(event.data);
          this.updateDiagnosticsState({
            status: 'connected',
            lastActivityAt: lastMessageAt,
            lastEvent: 'Mensaje recibido',
            lastEventAt: lastMessageAt,
          });
        }
      };

      // Manejo de errores
      this.ws.onerror = (error) => {
        const lastErrorAt = Date.now();
        console.error('Error en el websocket:', error);
        this.markDisconnected('Error en websocket');
        this.updateDiagnosticsState({
          lastErrorAt,
          lastErrorMessage: 'Evento de error',
          lastEvent: 'Error de conexion',
          lastEventAt: lastErrorAt,
        });

        // Fuerzo reconexión si el socket no se abrió correctamente tras el error, 
        // o si se quedó atascado en CONNECTING
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            if (!this.ws) return;
            if (this.ws.readyState !== WebSocket.OPEN) {
              this.forceReconnect('onerror_socket_not_open');
            }
          }, 1500);
        });
      };

      // Manejo de cierres: 
      this.ws.onclose = (event) => {
        console.warn(
          'WebSocket desconectado en carretera:',
          this.currentRoadId,
          'code:',
          event.code,
          'reason:',
          event.reason
        );

        this.markDisconnected('Socket cerrado');
        this.updateDiagnosticsState({
          lastCloseReason: `${event.code} - ${event.reason?.trim() || 'Sin motivo informado'}`,
          lastCloseAt: Date.now(),
          lastEvent: 'Socket cerrado',
          lastEventAt: Date.now(),
        });

        // Si el cierre fue manual o no tenemos carretera o la conexión ya estaba marcada como desconectada, no programo reconexión
        if (this.isManualClose) return;
        if (!this.currentRoadId) return;
        if (this.destroyed) return;

        // Programo reconexión
        this.scheduleReconnect(this.currentRoadId);
      };
    } catch (err) {
      const lastErrorAt = Date.now();
      console.error('Error al crear WebSocket:', err);
      this.markDisconnected('No se pudo crear el socket');
      this.updateDiagnosticsState({
        lastErrorAt,
        lastErrorMessage: 'Fallo al crear el socket',
        lastEvent: 'Fallo al crear el socket',
        lastEventAt: lastErrorAt,
      });

      if (!this.isManualClose && this.currentRoadId) {
        this.scheduleReconnect(this.currentRoadId);
      }
    }
  }

  /**
   * Método para gestionar las desconexiones del websocket
   */
  disconnect(): void {
    const roadId = this.currentRoadId;
    const roadName = this.currentRoadName;

    this.isManualClose = true;
    this.reconnectAttempts = 0;
    this.disconnectedSinceAt = null;
    this.resetActivityState();

    this.clearReconnectTimer();
    this.markDisconnected('Desconexion manual', false);
    this.closeSocketSilently();
    this.currentRoadId = null;
    this.currentRoadName = null;
    this.updateDiagnosticsState({
      status: 'disconnected',
      roadId,
      roadName,
      reconnectScheduledAt: null,
      connectedAt: null,
      disconnectedSinceAt: null,
      lastEvent: 'Desconexion manual',
      lastEventAt: Date.now(),
    });

    console.log('WebSocket desconectado manualmente');
  }

  /**
   * Método para enviar mensajes a través del websocket
   * EN DESUSO ACTUALMENTE
   * @param message: datos a enviar al servidor a través del websocket
   */
  send(message: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket no está conectado. No se puede enviar el mensaje');
    }
  }

  /**
   * Método que comprueba si el websocket está actualmente conectado y funcional
   * @returns true si el websocket está abierto y se ha marcado como conectado, false en caso contrario
   */
  isConnected(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN && this.connectionStatusSubject.value;
  }

  // -------------------------
  // MÉTODOS PRIVADOS
  // -------------------------

  /**
   * Método para programar una reconexión al websocket tras un cierre inesperado o error, con lógica de backoff exponencial y jitter
   * @param roadId 
   * @returns 
   */
  private scheduleReconnect(roadId: string): void {
    if (this.destroyed || this.isManualClose || !roadId) return;
    if (!navigator.onLine) {
      console.warn('Sin red. Esperando evento online para reconectar.');
      this.updateDiagnosticsState({
        status: 'disconnected',
        reconnectScheduledAt: null,
        lastEvent: 'Sin red. Esperando reconexion',
        lastEventAt: Date.now(),
      });
      return;
    }
    if (this.reconnectTimer) return;

    this.reconnectAttempts++;

    let delay: number;
    if (this.reconnectAttempts <= this.fastReconnectAttempts) {
      const exp = Math.min(
        this.maxReconnectMs,
        this.baseReconnectMs * Math.pow(2, this.reconnectAttempts - 1)
      );
      const jitter = Math.floor(Math.random() * 500);
      delay = exp + jitter;
    } else {
      const jitter = Math.floor(Math.random() * 1000);
      delay = this.slowReconnectMs + jitter;
    }

    this.clearReconnectTimer();
    console.warn(`Reconexión en ${delay} ms (intento ${this.reconnectAttempts}).`);
    const reconnectScheduledAt = Date.now() + delay;
    this.updateDiagnosticsState({
      status: 'reconnecting',
      roadId,
      roadName: this.currentRoadName,
      reconnectScheduledAt,
      lastEvent: `Reconectando en ${Math.ceil(delay / 1000)} s`,
      lastEventAt: Date.now(),
    });

    this.ngZone.runOutsideAngular(() => {
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        if (!this.currentRoadId || this.isManualClose || this.destroyed) return;
        this.connect(this.currentRoadId, this.currentRoadName);
      }, delay);
    });
  }

  /**
   * Método para forzar una reconexión inmediata al detectar que el socket está atascado en CONNECTING o 
   * no se ha abierto correctamente tras un error
   * 
   * @param reason: razón por la que se fuerza la reconexión
   */
  private forceReconnect(reason: string): void {
    if (this.destroyed || this.isManualClose || !this.currentRoadId) return;

    console.warn('Forzando reconexión WebSocket. Motivo:', reason);

    this.markDisconnected(`Reconectando por ${reason}`);
    this.updateDiagnosticsState({
      lastEvent: `Reconexion forzada: ${reason}`,
      lastEventAt: Date.now(),
    });
    this.clearReconnectTimer();
    this.closeSocketSilently();
    this.scheduleReconnect(this.currentRoadId);
  }

  /**
   * Método para iniciar el watchdog que periódicamente revisa la salud de la conexión websocket, detecta desconexiones, 
   * sockets atascados y condiciones de reconexión o recarga de página
   */
  private startWatchdog(): void {

    this.stopWatchdog();

    this.ngZone.runOutsideAngular(() => {
      this.watchdogTimer = setInterval(() => {
        if (this.destroyed || this.isManualClose) return;
        if (!this.currentRoadId) return;

        // Sin red: espero al evento online
        if (!navigator.onLine) {
          console.log('Watchdog: Sin conexión a Internet');
          this.markDisconnected('Watchdog: sin conexion');
          return;
        }

        // No hay socket: aseguro reconexión
        if (!this.ws) {
          console.log('Watchdog: WebSocket no existe');
          this.markDisconnected('Watchdog: socket no disponible');
          this.scheduleReconnect(this.currentRoadId);
          return;
        }

        // Socket cerrado o cerrándose
        if (
          this.ws.readyState === WebSocket.CLOSED ||
          this.ws.readyState === WebSocket.CLOSING
        ) {
          console.log('Watchdog: WebSocket cerrado o cerrándose');
          this.markDisconnected('Watchdog: socket cerrado');
          this.scheduleReconnect(this.currentRoadId);
          return;
        }

        // Socket atascado conectando demasiado tiempo
        if (
          this.ws.readyState === WebSocket.CONNECTING &&
          this.lastConnectAttemptAt &&
          Date.now() - this.lastConnectAttemptAt > this.maxConnectingTimeMs
        ) {
          console.log('Watchdog: WebSocket atascado conectando');
          this.forceReconnect('stuck_connecting');
          return;
        }

        // Si está abierto, marco conectado
        if (this.ws.readyState === WebSocket.OPEN) {
          const referenceActivityAt = this.lastActivityAt ?? this.diagnosticsSubject.value.connectedAt;
          const inactivityReconnectMs = this.getInactivityReconnectMs();

          if (referenceActivityAt) {
            const silenceMs = Date.now() - referenceActivityAt;
            if (silenceMs >= inactivityReconnectMs) {
              console.warn(
                `Watchdog: WebSocket inactivo durante ${Math.round(silenceMs / 1000)} segundos`
              );
              this.updateDiagnosticsState({
                status: 'disconnected',
                inactivityDetectedAt: Date.now(),
                inactivityDetected: `Sin mensajes del websocket durante ${Math.round(silenceMs / 60_000)} min (umbral ${Math.round(inactivityReconnectMs / 60_000)} min)`,
                lastEvent: 'Inactividad detectada por watchdog',
                lastEventAt: Date.now(),
              });
              this.forceReconnect('socket_inactive');

              // Si estamos en la pantalla de centro actividad, lanzamos evento de refresco de datos
              if (this.router.url.includes('center-activity')) {
                // Refrescamos los datos de la pantalla
                this.stateService.triggerCenterActivityRefresh();
              }
              return;
            }
          }

          this.connectionStatusSubject.next(true);
          this.updateDiagnosticsState({
            status: 'connected',
          });
        }

        // Último recurso: si llevamos mucho desconectados, recarga controlada
        if (
          !this.hasForcedReload &&
          this.disconnectedSinceAt &&
          Date.now() - this.disconnectedSinceAt > this.forceReloadAfterMs &&
          document.visibilityState === 'visible'
        ) {
          this.hasForcedReload = true;
          this.updateDiagnosticsState({
            lastEvent: 'Recarga forzada por watchdog',
            lastEventAt: Date.now(),
          });
          console.warn('Watchdog: Se fuerza recarga de página como último recurso');
          window.location.reload();
        }
      }, this.watchdogIntervalMs);
    });
  }

  /**
   * Método para detener el watchdog, limpiando el timer asociado y evitando que siga ejecutándose tras destruir el servicio
   */
  private stopWatchdog(): void {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  /**
   * Método para limpiar el timer de reconexión programado, evitando múltiples timers activos o reconexiones no deseadas
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.updateDiagnosticsState({
      reconnectScheduledAt: null,
    });
  }

  /**
   * Método para cerrar el socket de forma silenciosa, sin disparar eventos ni marcar como desconectado 
   * (usado para cambios de carretera o limpieza)
   */
  private closeSocketSilently(): void {
    if (!this.ws) return;

    try {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;

      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, 'client_cleanup');
      }
    } catch (error) {
      console.warn('Error cerrando socket anterior:', error);
    } finally {
      this.ws = null;
    }
  }

  /**
   * Método para marcar la conexión como desconectada
   */
  private markDisconnected(
    lastEvent = 'Desconectado',
    trackDisconnection = true
  ): void {
    const disconnectedSinceAt =
      trackDisconnection ? (this.disconnectedSinceAt ?? Date.now()) : null;

    this.connectionStatusSubject.next(false);
    this.disconnectedSinceAt = disconnectedSinceAt;
    this.updateDiagnosticsState({
      status: 'disconnected',
      roadId: this.currentRoadId,
      roadName: this.currentRoadName,
      connectedAt: null,
      disconnectedSinceAt,
      lastEvent,
      lastEventAt: Date.now(),
    });
  }

  /**
   * Registra actividad real observada en el socket para que el watchdog pueda detectar inactividad
   * Exclusivamente cuando se abre la conexión o se reciben mensajes
   */
  private recordSocketActivity(activityAt: number): void {
    this.lastActivityAt = activityAt;
    this.updateDiagnosticsState({
      lastActivityAt: activityAt,
    });
  }

  /**
   * Limpia el estado temporal de actividad cuando se cambia de socket o se desconecta manualmente
   */
  private resetActivityState(): void {
    this.lastActivityAt = null;
    this.updateDiagnosticsState({
      lastActivityAt: null,
    });
  }

  /**
   * Devuelve el timeout de inactividad aplicable según la franja horaria local:
   * - 08:00 a 10:00 => 5 minutos
   * - 13:00 a 15:00 => 5 minutos
   * - resto del día => 15 minutos
   */
  private getInactivityReconnectMs(referenceDate: Date = new Date()): number {
    const totalMinutes = referenceDate.getHours() * 60 + referenceDate.getMinutes();
    const isMorningPeak = totalMinutes >= 8 * 60 && totalMinutes < 10 * 60;
    const isMiddayPeak = totalMinutes >= 13 * 60 && totalMinutes < 15 * 60;

    if (isMorningPeak || isMiddayPeak) {
      return this.peakInactivityReconnectMs;
    }

    return this.defaultInactivityReconnectMs;
  }

  /**
   * Método para construir la URL del websocket a partir de la carretera
   * @param roadId: identificador de la carretera para la que se quieren recibir alertas
   * @return URL completa del websocket con el esquema correcto (ws o wss) y el parámetro de carretera
   */
  private getWebSocketUrl(roadId: string): string {
    const apiUri = environment.webSocketConfig.uri;
    const wsUri = apiUri.replace(
      /^https?:\/\//,
      (match: string) => (match === 'https://' ? 'wss://' : 'ws://')
    );
    return `${wsUri}?roadId=${encodeURIComponent(roadId)}`;
  }

  /**
   * Contructor por defecto para el estado de diagnóstico del websocket
   */
  private buildInitialDiagnosticsState(): WebSocketDiagnostics {
    return {
      status: 'disconnected',
      roadId: null,
      roadName: null,
      reconnectScheduledAt: null,
      lastConnectAttemptAt: null,
      connectedAt: null,
      disconnectedSinceAt: null,
      lastActivityAt: null,
      inactivityDetected: null,
      inactivityDetectedAt: null,
      lastErrorAt: null,
      lastErrorMessage: null,
      lastCloseAt: null,
      lastCloseReason: null,
      lastEvent: 'Servicio inicializado',
      lastEventAt: Date.now()
    };
  }

  /**
   * Método para actualizar el estado de diagnóstico del websocket de forma parcial, 
   * manteniendo los campos no actualizados
   */
  private updateDiagnosticsState(patch: Partial<WebSocketDiagnostics>): void {
    this.diagnosticsSubject.next({
      ...this.diagnosticsSubject.value,
      ...patch,
    });
  }
}
